import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  deleteInsuranceProvider,
  fetchInsuranceProviders,
  refreshInsuranceProviderList,
} from "src/store/reducers/insuranceProviders";
import ConfirmationModal from "src/layouts/modals/ConfirmationModal";
import { toast } from "react-toastify";
import InsuranceProviderFormCanvas from "./InsuranceProviderFormCanvas";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import BottomPagination from "src/jsx/customComponents/pagination/BottomPagination";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "react-bootstrap";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";

const ListInsuranceProviders = () => {
  const dispatch = useAppDispatch();
  const providerList = useAppSelector((state) => state.insuranceProviders.data);
  const providerListStatus = useAppSelector(
    (state) => state.insuranceProviders.status
  );
  const [editId, setEditId] = useState("");
  const [deleteProviderId, setDeleteProviderId] = useState<string>();
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState(false);
  const keywordRef = useRef("");

  const { handleChange, values, handleSubmit } = useFormik({
    initialValues: { keyword: "" },
    enableReinitialize: true,
    validationSchema: Yup.object({ keyword: Yup.string() }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setPending(true);
      keywordRef.current = values.keyword;
      setPage(1);
      dispatch(fetchInsuranceProviders({ page: 1, keyword: values.keyword }));
      setPending(false);
    },
  });

  const retry = useCallback(() => {
    dispatch(fetchInsuranceProviders({ page, keyword: keywordRef.current }));
  }, [dispatch, page]);

  const cancelDelete = () => {
    setDeleteProviderId(undefined);
  };

  const confirmDelete = useCallback(async () => {
    if (deleteProviderId) {
      try {
        await dispatch(deleteInsuranceProvider(deleteProviderId)).unwrap();
        toast.success("Insurance provider is deleted successfully");
        setDeleteProviderId(undefined);
        setPage(1);
        retry();
      } catch (e: any) {
        FormikAPIErrorHandler(e.message, undefined, toast.error);
      }
    }
  }, [deleteProviderId, dispatch, retry]);

  useEffect(() => {
    if (providerListStatus === "idle") {
      retry();
    }
  }, [providerListStatus, retry]);

  useEffect(() => {
    return () => {
      dispatch(refreshInsuranceProviderList());
    };
  }, [dispatch]);

  const setPageAndFetch = useCallback(
    (newPage: number) => {
      setPage(newPage);
      dispatch(
        fetchInsuranceProviders({ page: newPage, keyword: keywordRef.current })
      );
    },
    [dispatch]
  );

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12 col-xxl-12">
            <div className="card h-auto">
              <form onSubmit={handleSubmit}>
                <div className="row container-fluid">
                  <div className="col-xl-3 mb-3">
                    <CustomInputWithLabel
                      id="keyword"
                      placeholder={"Search"}
                      handleChange={handleChange}
                      value={values.keyword}
                    />
                  </div>
                  <div className="col-xl-2 mb-3">
                    <Button
                      className="form-control"
                      disabled={pending}
                      type="submit"
                    >
                      {pending ? "Loading" : "Submit"}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12 col-xxl-12">
            <div className="card h-auto">
              <div className="card-header">
                <h4 className="heading mb-0">Insurance Providers</h4>
                <Link
                  to={"#"}
                  className="btn btn-primary btn-sm"
                  data-bs-toggle="offcanvas"
                  onClick={() => setEditId("Add")}
                >
                  + Add Insurance Provider
                </Link>
              </div>
              <div className="card-body">
                {providerListStatus === "success" ? (
                  providerList?.results && providerList?.results.length > 0 ? (
                    <div className="table-responsive active-projects">
                      <div
                        id="projects-tbl_wrapper"
                        className="dataTables_wrapper no-footer"
                      >
                        <table
                          id="projects-tbl"
                          className="table table-hover dataTable no-footer mb-0"
                        >
                          <thead>
                            <tr>
                              <th>Provider Name</th>
                              <th>Provider Code</th>
                              <th>Contact Person</th>
                              <th>Contact No.</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {providerList?.results?.map((provider) => (
                              <tr key={provider.id}>
                                <td>{provider.provider_name}</td>
                                <td>{provider.provider_code}</td>
                                <td>{provider.contact_person_name}</td>
                                <td>{provider.contact_number}</td>
                                <td>
                                  <NavLink
                                    className="btn btn-warning shadow btn-xs sharp me-1"
                                    to={`/insurance-providers/view/${provider.id}`}
                                    title="View"
                                  >
                                    <i className={`fas fa-paper-plane`} />
                                  </NavLink>{" "}
                                  <NavLink
                                    className="btn btn-primary shadow btn-xs sharp me-1"
                                    onClick={() => setEditId(provider.id)}
                                    to={"#"}
                                    title="Edit"
                                  >
                                    <i className={`fas fa-pen`} />
                                  </NavLink>{" "}
                                  <NavLink
                                    className="btn btn-danger shadow btn-xs sharp"
                                    onClick={() => {
                                      setDeleteProviderId(provider.id);
                                    }}
                                    to={`#`}
                                    title="Delete"
                                  >
                                    <i className={`fas fa-x`} />
                                  </NavLink>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {providerList?.count ? (
                          <BottomPagination
                            count={providerList?.count}
                            page={page}
                            setPageAndFetch={setPageAndFetch}
                          />
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center">
                      <strong>NA</strong>
                    </div>
                  )
                ) : providerListStatus === "pending" ? (
                  <div className="d-flex align-items-center">
                    <strong>Loading...</strong>
                    <div
                      className="spinner-border ms-auto"
                      role="status"
                      aria-hidden="true"
                    ></div>
                  </div>
                ) : providerListStatus === "error" ? (
                  <div className="d-flex align-items-center">
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={retry}
                    >
                      Retry
                    </button>{" "}
                    &nbsp;
                    <strong onClick={retry}>Please try again </strong>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        show={typeof deleteProviderId !== "undefined"}
        handleClose={cancelDelete}
        confirmClose={confirmDelete}
      />
      <InsuranceProviderFormCanvas
        show={editId !== ""}
        hide={() => setEditId("")}
        editId={editId}
        Title={editId === "Add" ? "Add Insurance Provider" : "Edit Insurance Provider"}
        providerData={providerList?.results?.find(
          (provider) => provider.id === editId
        )}
      />
    </>
  );
};

export default ListInsuranceProviders;
